import { Row, Col, Tabs as AntTabs, ConfigProvider, Button, Card } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import { ApplicationState } from "../../redux/store";
import {
  useGetAllDocumentQuery,
  useGetDocumentQuery,
} from "../../redux/api/document/document";
import DocumentComment from "../versions/document-comments";
import DocumentVersionsV1 from "../versions/document-versions-v1";
import DocumentActiviesV1 from "../versions/document-activities-v1";
import AttachmentViewer from "../../components/attachment-viewer";
import { BreadcrumbGroup } from "@cloudscape-design/components";
import DocumentEditForm from "../versions/document-edit-form";

export default function EditDocument({
  providerdocumentId: initialProviderDocumentId,
  toggleDrawer,
}: {
  providerdocumentId?: string;
  toggleDrawer?: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { documentId } = useParams();

  const [activeKey, setActiveKey] = useState("details");
  const [uploadedFile, setUploadedFile] = useState<File>();
  const [action, setAction] = useState<"next" | "previous" | null>(null);
  const [currentProviderDocumentId, setCurrentProviderDocumentId] = useState(
    initialProviderDocumentId || documentId
  );

  const isProvider = location.pathname.includes("manageprovider");
  const { providerId } = useParams();

  const currentId = isProvider ? currentProviderDocumentId : documentId;
  const {
    data: documentList,
    isFetching,
    isLoading,
  } = useGetAllDocumentQuery(
    !!currentId
      ? {
          inactiveProvider: false,
          pageSize: 1,
          pageNumber: 0,
          sortBy: "id",
          direction: "ASC",
          action: action ?? "next",
          anchorDocumentId: currentId,
          providerId: isProvider ? providerId : undefined,
        }
      : skipToken,
    {
      pollingInterval: 0,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: document } = useGetDocumentQuery(
    currentId ? { documentId: currentId } : skipToken
  );

  const hasPermission = useSelector(
    (state: ApplicationState) =>
      state.ui.is_owner ||
      (state.ui.permissions?.includes("provider.edit") ?? false)
  );

  useEffect(() => {
    setActiveKey("details");
    setUploadedFile(undefined);
    setAction(null);
    if (isProvider) {
      setCurrentProviderDocumentId(initialProviderDocumentId || documentId);
    }
  }, [documentId, initialProviderDocumentId, isProvider]);

  useEffect(() => {
    if (
      !isFetching &&
      !isLoading &&
      documentList?.documents?.[0]?.id &&
      action
    ) {
      const nextDocumentId = documentList.documents[0].id;
      if (isProvider) {
        setCurrentProviderDocumentId(nextDocumentId);
        setAction(null);
      } else {
        navigate(`/safe/${nextDocumentId}/edit`);
        setAction(null);
      }
    }
  }, [documentList, action, isFetching, isLoading, navigate, isProvider]);

  const handlePrevious = () => setAction("previous");
  const handleNext = () => setAction("next");

  return (
    <div
      style={{
        padding: "12px 24px 12px 24px",
      }}
    >
      <div
        style={{
          padding: "0 0 16px 0",
          position: "relative",
        }}
      >
        <BreadcrumbGroup
          items={[
            {
              text: isProvider ? "Provider" : "safe",
              href: isProvider
                ? `/manageprovider/${
                    documentList?.documents?.at(0)?.provider?.id
                  }`
                : "/safe",
            },
            { text: "Edit", href: "" },
          ]}
          ariaLabel="Breadcrumbs"
        />
      </div>
      <ConfigProvider
        theme={{
          components: {
            Tabs: {
              itemSelectedColor: "#2B6BE6",
              cardBg: "#F5F5F5",
              colorBgContainer: "#2B6BE6",
              inkBarColor: "#2B6BE6",
              colorBorderSecondary: "#c6c6cd",
              fontSize: 16,
            },
          },
        }}
      >
        <AntTabs
          activeKey={activeKey}
          onChange={setActiveKey}
          size="large"
          items={[
            {
              label: <span style={{ fontWeight: "700" }}>Document</span>,
              key: "details",
              children: (
                <>
                  <Row gutter={24}>
                    {activeKey === "details" && (
                      <div
                        style={{
                          marginTop: "auto",
                          padding: "0 10px 5px 10px",
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Button
                          type="primary"
                          onClick={handlePrevious}
                          disabled={isFetching || isLoading}
                        >
                          Previous
                        </Button>
                        <Button
                          type="primary"
                          onClick={handleNext}
                          disabled={isFetching || isLoading}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </Row>
                  <Row gutter={[24, 24]}>
                    <Col span={12}>
                      <DocumentEditForm
                        id={isProvider ? currentProviderDocumentId : documentId}
                        uploadedFile={uploadedFile}
                        title="Document details"
                        toggleDrawer={isProvider ? toggleDrawer : undefined}
                        isProvider={isProvider}
                      />
                    </Col>
                    <Col span={12}>
                      <AttachmentViewer
                        attachment={document?.attachment}
                        onFileChange={(file) => {
                          setUploadedFile(file);
                        }}
                        file={uploadedFile}
                      />
                    </Col>
                  </Row>
                </>
              ),
            },
            {
              label: <span style={{ fontWeight: "700" }}>Versions</span>,
              key: "versions",
              children: (
                <DocumentVersionsV1
                  providerdocumentId={currentProviderDocumentId}
                />
              ),
            },
            {
              label: <span style={{ fontWeight: "700" }}>Audit logs</span>,
              key: "audit_logs",
              children: (
                <DocumentActiviesV1
                  providerdocumentId={currentProviderDocumentId}
                />
              ),
            },
            {
              label: <span style={{ fontWeight: "700" }}>Comments</span>,
              key: "comments",
              children: (
                <DocumentComment
                  providerdocumentId={currentProviderDocumentId}
                />
              ),
            },
          ]}
        />
      </ConfigProvider>
    </div>
  );
}
